import { useEffect, useLayoutEffect, useRef, useState } from "react"

/** One step of the run as the clock sees it. */
export interface ClockStep {
  id: string
  /** The step is being worked on right now (the run is going and this is the step it is on). */
  active: boolean
  /** The server's own measure for a landed step (stage.landed duration_ms), when it sent one. */
  durationMs?: number
}

interface Seg { acc: number; start?: number }

/**
 * Live elapsed time per step. The backend's events carry no per-step start/end stamps (a landed stage
 * has only `duration_ms`), so the page records its own: the moment a step becomes active it starts a
 * segment, the moment it stops (landed, or the run paused for the person) the segment is closed and
 * the time freezes. A 1s interval re-renders only while some step is open. A landed step that this tab
 * never watched (a reload) falls back to the server's `duration_ms`, else null (shown as a dash).
 */
export function useStepClock(steps: ClockStep[]): (id: string) => number | null {
  const segs = useRef(new Map<string, Seg>())
  const [now, setNow] = useState(() => Date.now())
  const sig = steps.map((s) => `${s.id}:${s.active ? 1 : 0}`).join("|")
  useLayoutEffect(() => {
    const t = Date.now()
    for (const s of steps) {
      const seg = segs.current.get(s.id) ?? { acc: 0 }
      if (s.active && seg.start == null) seg.start = t
      else if (!s.active && seg.start != null) { seg.acc += t - seg.start; seg.start = undefined }
      segs.current.set(s.id, seg)
    }
    setNow(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig])
  const open = steps.some((s) => s.active)
  useEffect(() => {
    if (!open) return
    const h = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(h)
  }, [open])
  return (id: string) => {
    const seg = segs.current.get(id)
    const step = steps.find((s) => s.id === id)
    const watched = seg ? seg.acc + (seg.start != null ? Math.max(0, now - seg.start) : 0) : 0
    if (step?.active) return watched
    if (step?.durationMs != null) return step.durationMs
    return seg && (seg.acc > 0 || seg.start != null) ? watched : null
  }
}

/** 0:07, 1:32, 12:05. */
export function formatElapsed(ms: number | null): string {
  if (ms == null) return "–"
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
}
